import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/es";
import localeData from "dayjs/plugin/localeData";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {
  IonCol,
  IonGrid,
  IonItem,
  IonList,
  IonListHeader,
  IonRow,
} from "@ionic/react";
import { Card, Typography } from "@mui/joy";
import {
  borrarObjetosDuplicados,
  ordenarArrayObjetosByDate,
} from "../../utils/funcionalidadesArrays";
import {
  convertToPlain,
  obtenerNombreDia,
} from "../../utils/funcionalidadesStrings";
import {
  Column,
  Container,
} from "../../components/basicsComponents/MaterialGridComponents";
import CustomCalendar from "../../components/basicsComponents/Calendar";
import CustomDesplegable from "../../components/basicsComponents/CustomDesplegable/CustomDesplegable";
import { DialogoConfirmacion } from "../../components/basicsComponents/DialogoConfirmacion/DialogoConfirmacion";
import StyledButton from "../../components/basicsComponents/StyledButton/StyledButton";
import CustomToast from "../../components/basicsComponents/CustomToast/CustomToast";
import LoadingBackdrop from "../../components/basicsComponents/LoadingBackdrop/LoadingBackdrop";
import useTituloPagina from "../../hooks/tituloPagina";
import "./ReservaTurnoEstudios.css";

dayjs.extend(weekOfYear);
dayjs.extend(localeData);
dayjs.locale("es");

const obtenerCampo = (objeto, ...claves) => {
  const claveEncontrada = claves.find(
    (clave) => objeto?.[clave] !== undefined && objeto?.[clave] !== null,
  );
  return claveEncontrada ? objeto[claveEncontrada] : undefined;
};

const normalizarEstudio = (estudio) => ({
  ...estudio,
  codigo: obtenerCampo(estudio, "codigo", "Codigo"),
  nombre: obtenerCampo(estudio, "nombre", "Nombre"),
  preparacion: obtenerCampo(estudio, "preparacion", "Preparacion") || "",
  text:
    obtenerCampo(estudio, "nombre", "Nombre") ||
    `Estudio ${obtenerCampo(estudio, "codigo", "Codigo")}`,
});

const normalizarTurnoEquipo = (turno) => ({
  codigo: obtenerCampo(turno, "codigo", "Codigo"),
  fecha: obtenerCampo(turno, "fecha", "Fecha"),
  hora: obtenerCampo(turno, "hora", "Hora"),
  equipoCod: obtenerCampo(turno, "equipoCod", "EquipoCod", "equipo", "Equipo"),
  equipoNom: obtenerCampo(turno, "equipoNom", "EquipoNom"),
  libre: Boolean(obtenerCampo(turno, "libre", "Libre")),
  paciente: obtenerCampo(turno, "paciente", "Paciente"),
  mutual: obtenerCampo(turno, "mutual", "Mutual"),
  asistio: Boolean(obtenerCampo(turno, "asistio", "Asistio")),
  servicio: obtenerCampo(turno, "servicio", "Servicio"),
  cancelado: Boolean(obtenerCampo(turno, "cancelado", "Cancelado")),
  atendidoHora: obtenerCampo(turno, "atendidoHora", "AtendidoHora"),
  fechaOrigen: obtenerCampo(turno, "fechaOrigen", "FechaOrigen"),
  horaOrigen: obtenerCampo(turno, "horaOrigen", "HoraOrigen"),
  origenTurno: obtenerCampo(turno, "origenTurno", "OrigenTurno"),
  entreTurno: Boolean(obtenerCampo(turno, "entreTurno", "EntreTurno")),
  reservado: Boolean(obtenerCampo(turno, "reservado", "Reservado")),
  telemedicina: Boolean(obtenerCampo(turno, "telemedicina", "Telemedicina")),
  turnosWeb: obtenerCampo(turno, "turnosWeb", "TurnosWeb"),
});

export default function ReservaTurnoEstudios() {
  const [usuario, setUsuario] = useState({});
  const [cargando, setCargando] = useState(false);
  const [cargandoEstudios, setCargandoEstudios] = useState(false);
  const [cargandoAgenda, setCargandoAgenda] = useState(false);
  const [estudios, setEstudios] = useState([]);
  const [turnosPaciente, setTurnosPaciente] = useState([]);
  const [estudioSeleccionado, setEstudioSeleccionado] = useState(-1);
  const [mostrarTurnoEstudioReservado, setMostrarTurnoEstudioReservado] =
    useState(false);
  const [fechasFuturas, setFechasFuturas] = useState([]);
  const [fechaSeleccionada, setFechaSeleccionada] = useState("");
  const [turnosDisponibles, setTurnosDisponibles] = useState([]);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState({});
  const [abrirDesplegableEstudios, setAbrirDesplegableEstudios] =
    useState(false);
  const [abrirModalConfirmarReserva, setAbrirModalConfirmarReserva] =
    useState(false);
  const [abrirModalPostReserva, setAbrirModalPostReserva] = useState(false);
  const [toast, setToast] = useState({ open: false, mensaje: "", tipo: "" });
  const history = useHistory();

  useTituloPagina();

  const mostrarNotificacion = (abrir, mensaje, tipo) => {
    if (abrir) {
      setToast({ open: true, mensaje, tipo });
    } else {
      setToast({ open: false, mensaje: "", tipo: "" });
    }
  };

  const getConfig = (paciente) => ({
    headers: { Authorization: `Bearer ${paciente.token}` },
  });

  const limpiarAgenda = () => {
    setFechasFuturas([]);
    setFechaSeleccionada("");
    setTurnosDisponibles([]);
    setTurnoSeleccionado({});
    setAbrirModalConfirmarReserva(false);
  };

  const traerEstudios = async (paciente) => {
    const url = localStorage.getItem("urlAxioPP");

    try {
      const response = await axios.get(`${url}Estudios`, getConfig(paciente));
      const estudiosDisponibles = Array.isArray(response.data)
        ? response.data.map(normalizarEstudio)
        : [];

      setEstudios(estudiosDisponibles);
      return estudiosDisponibles;
    } catch (e) {
      setEstudios([]);
      mostrarNotificacion(
        true,
        "Se produjo un error al consultar los estudios disponibles",
        "rojo",
      );
      return [];
    }
  };

  const traerTurnosPaciente = async (paciente) => {
    const url = localStorage.getItem("urlAxioPP");
    const config = getConfig(paciente);

    try {
      const serviciosResponse = await axios.get(`${url}Servicios`, config);
      const servicio = Array.isArray(serviciosResponse.data)
        ? serviciosResponse.data[0]
        : null;

      if (!servicio?.codigo) {
        setTurnosPaciente([]);
        return [];
      }

      const response = await axios.get(
        `${url}Turnos/TurnosPaciente/?paciente=${paciente.codigo}&servicio=${servicio.codigo}`,
        config,
      );

      const turnos = Array.isArray(response.data) ? response.data : [];
      setTurnosPaciente(turnos);
      return turnos;
    } catch (e) {
      mostrarNotificacion(
        true,
        "Ocurrio un problema al consultar sus turnos registrados",
        "rojo",
      );
      setTurnosPaciente([]);
      return [];
    }
  };

  const consultarTurnosEquipoFecha = async (url, config, estudioCodigo) => {
    const response = await axios.get(
      `${url}Turnos/TurnosEquipoFecha/?estudio=${estudioCodigo}&desde=${dayjs().format(
        "YYYY/MM/DD HH:mm",
      )}&hasta=${dayjs().add(3, "month").format("YYYY/MM/DD HH:mm")}`,
      config,
    );

    return Array.isArray(response.data) ? response.data : [];
  };

  const consultarTurnosEquipo = async (
    url,
    config,
    estudioCodigo,
    mutualCodigo,
  ) => {
    const response = await axios.get(
      `${url}Turnos/TurnosEquipo?mutual=${mutualCodigo}&estudio=${estudioCodigo}`,
      config,
    );

    return Array.isArray(response.data) ? response.data : [];
  };

  const obtenerTurnosEquipo = async (paciente, estudioCodigo) => {
    const url = localStorage.getItem("urlAxioPP");
    const config = getConfig(paciente);
    let turnos = [];

    try {
      turnos = await consultarTurnosEquipoFecha(url, config, estudioCodigo);

      if (turnos.length === 0) {
        turnos = await consultarTurnosEquipo(
          url,
          config,
          estudioCodigo,
          paciente.mutual,
        );
      }

      return turnos
        .map(normalizarTurnoEquipo)
        .filter(
          (turno) =>
            turno.libre &&
            !turno.cancelado &&
            !turno.reservado &&
            !turno.entreTurno &&
            turno.turnosWeb !== false,
        );
    } catch (error) {
      throw new Error(
        "Error al obtener los turnos disponibles del estudio: " + error.message,
      );
    }
  };

  const filtrarTurnosPorFechaYHora = (turnos) =>
    turnos.filter((turno) => {
      const fechaTurno = dayjs(turno.fecha);
      const hoy = dayjs();

      if (!fechaTurno.isSame(hoy, "day") && !fechaTurno.isAfter(hoy, "day")) {
        return false;
      }

      if (!fechaTurno.isSame(hoy, "day")) {
        return true;
      }

      return (
        dayjs(turno.hora).format("HH:mm") >
        dayjs().add(2, "hour").format("HH:mm")
      );
    });

  const obtenerFechasTurnos = (turnosFiltrados) =>
    borrarObjetosDuplicados(turnosFiltrados, "fecha", true)
      .filter((turno) => turno)
      .map((turno) => ({ fecha: turno }));

  const obtenerTurnosUnicosPorHorario = (turnos) => {
    const turnosPorHorario = new Map();

    turnos.forEach((turno) => {
      const claveHorario = `${dayjs(turno.fecha).format("YYYY-MM-DD")}#${dayjs(
        turno.hora,
      ).format("HH:mm")}`;
      const turnoActual = turnosPorHorario.get(claveHorario);

      if (!turnoActual) {
        turnosPorHorario.set(claveHorario, turno);
        return;
      }

      const equipoActual = Number(
        turnoActual.equipoCod || Number.MAX_SAFE_INTEGER,
      );
      const equipoNuevo = Number(turno.equipoCod || Number.MAX_SAFE_INTEGER);
      const codigoActual = Number(
        turnoActual.codigo || Number.MAX_SAFE_INTEGER,
      );
      const codigoNuevo = Number(turno.codigo || Number.MAX_SAFE_INTEGER);

      if (
        equipoNuevo < equipoActual ||
        (equipoNuevo === equipoActual && codigoNuevo < codigoActual)
      ) {
        turnosPorHorario.set(claveHorario, turno);
      }
    });

    return Array.from(turnosPorHorario.values());
  };

  const tieneTurnoFuturoMismoEstudio = (estudioCodigo) => {
    if (!Array.isArray(turnosPaciente) || turnosPaciente.length === 0) {
      return false;
    }

    return turnosPaciente.some((turno) => {
      const codigoEstudioTurno = obtenerCampo(turno, "estudio", "Estudio");
      const fechaTurno = dayjs(obtenerCampo(turno, "fecha", "Fecha"));
      const cancelado = Boolean(obtenerCampo(turno, "cancelado", "Cancelado"));
      const activo = obtenerCampo(turno, "aCancelar", "ACancelar");
      const asistio = Boolean(obtenerCampo(turno, "asistio", "Asistio"));
      const esFuturoOVigente =
        (fechaTurno.isSame(dayjs(), "day") &&
          dayjs(obtenerCampo(turno, "hora", "Hora")).format("HH:mm") >=
            dayjs().format("HH:mm")) ||
        fechaTurno.isAfter(dayjs(), "day");

      return (
        Number(codigoEstudioTurno) === Number(estudioCodigo) &&
        activo !== false &&
        !cancelado &&
        !asistio &&
        esFuturoOVigente
      );
    });
  };

  const traerAgendaEstudio = async (paciente, estudioCodigo) => {
    limpiarAgenda();
    setCargandoAgenda(true);

    try {
      const turnosEquipo = await obtenerTurnosEquipo(paciente, estudioCodigo);
      const turnosFiltrados = filtrarTurnosPorFechaYHora(turnosEquipo);
      const turnosVisibles = obtenerTurnosUnicosPorHorario(turnosFiltrados);
      const fechasTurnos = obtenerFechasTurnos(turnosVisibles);
      const turnosProximos = fechasTurnos.map((fecha) => ({
        date: dayjs(fecha.fecha).format("YYYY-MM-DD"),
        cantTurnos: turnosVisibles.filter((turno) =>
          dayjs(turno.fecha).isSame(dayjs(fecha.fecha), "day"),
        ).length,
        textColor: "#e6e5f1",
        backgroundColor: "#695194",
      }));

      if (turnosProximos.length > 0) {
        const fechasOrdenadas = ordenarArrayObjetosByDate(
          turnosProximos,
          "date",
        );
        setFechasFuturas(fechasOrdenadas);
        setFechaSeleccionada(fechasOrdenadas[0].date);
        setTurnosDisponibles(ordenarArrayObjetosByDate(turnosVisibles, "hora"));
      } else {
        mostrarNotificacion(
          true,
          "No hay turnos disponibles para el estudio seleccionado",
          "amarillo",
        );
      }
    } catch (error) {
      mostrarNotificacion(true, "Error: " + error.message, "rojo");
    } finally {
      setCargandoAgenda(false);
    }
  };

  const actualizarTurno = async () => {
    const url = localStorage.getItem("urlAxioPP");

    try {
      const config = getConfig(usuario);

      if (turnoSeleccionado?.codigo) {
        const actualizarTurnoResponse = await axios.patch(
          `${url}Turnos/DarTurno/${turnoSeleccionado.codigo}`,
          {
            codigo: turnoSeleccionado.codigo,
            paciente: usuario.codigo,
            mutual: usuario.mutual,
            estudio: estudioSeleccionado,
            equipo: turnoSeleccionado.equipoCod || null,
            prestador: 0,
            telemedicina: turnoSeleccionado.telemedicina || false,
            observaciones: "Turno reservado via web por el paciente",
          },
          config,
        );

        if (actualizarTurnoResponse.status === 200) {
          await traerTurnosPaciente(usuario);
          mostrarNotificacion(
            true,
            "El turno se reservo correctamente",
            "verde",
          );
          setAbrirModalPostReserva(true);
          return;
        }
      }

      mostrarNotificacion(
        true,
        "Ocurrio un problema al intentar reservar el turno",
        "rojo",
      );
    } catch (error) {
      const mensaje = error?.response?.data || error.message;
      mostrarNotificacion(true, "Error: " + mensaje, "rojo");
    }
  };

  useEffect(() => {
    const inicializarPantalla = async () => {
      setCargando(true);
      setCargandoEstudios(true);

      try {
        const sesion = JSON.parse(sessionStorage.getItem("ppUL"));
        if (!sesion) {
          history.push({ pathname: "/ErrorPage" });
          return;
        }

        setUsuario(sesion);
        document.title = localStorage.getItem("tituloWeb");
        await Promise.all([traerEstudios(sesion), traerTurnosPaciente(sesion)]);
      } catch (e) {
        mostrarNotificacion(
          true,
          "Se produjo un error al preparar la pantalla de reserva por estudios",
          "rojo",
        );
        history.push({ pathname: "/ErrorPage" });
      } finally {
        setCargandoEstudios(false);
        setCargando(false);
      }
    };

    inicializarPantalla();
  }, [history]);

  useEffect(() => {
    if (
      !cargando &&
      !cargandoEstudios &&
      estudios.length > 0 &&
      estudioSeleccionado === -1
    ) {
      setAbrirDesplegableEstudios(true);
    }
  }, [cargando, cargandoEstudios, estudios, estudioSeleccionado]);

  const handleChangeSelect = async (value, select) => {
    switch (select) {
      case "Estudio": {
        setEstudioSeleccionado(value);
        setMostrarTurnoEstudioReservado(false);
        limpiarAgenda();

        if (value !== -1 && usuario?.token) {
          if (tieneTurnoFuturoMismoEstudio(value)) {
            setMostrarTurnoEstudioReservado(true);
            mostrarNotificacion(
              true,
              "Ya tiene un turno futuro reservado para este estudio",
              "amarillo",
            );
            break;
          }

          await traerAgendaEstudio(usuario, value);
        }
        break;
      }
      default:
        mostrarNotificacion(true, "Seleccion invalida", "rojo");
        break;
    }
  };

  const handleChangeFecha = (value) => {
    setFechaSeleccionada(value);
  };

  const handleClickSeleccionarTurno = (turno) => {
    setTurnoSeleccionado(turno);
    setAbrirModalConfirmarReserva(true);
  };

  const handleClickConfirmarHacerReserva = async () => {
    setCargando(true);
    await actualizarTurno();
    setCargando(false);
  };

  const handlePostTurnoReservado = (accion) => {
    setTurnoSeleccionado({});
    setAbrirModalPostReserva(false);

    switch (accion) {
      case "nuevoTurno":
        window.location.reload();
        break;
      case "turnosPaciente":
        history.push({ pathname: "/portal/Turnos" });
        break;
      default:
        history.push({ pathname: "/portal/Turnos" });
        break;
    }
  };

  const estudioActual =
    estudios.find(
      (estudio) => Number(estudio.codigo) === Number(estudioSeleccionado),
    ) || null;

  const renderTurnos = () => {
    const turnos = turnosDisponibles.filter(
      (turno) =>
        dayjs(turno.fecha).format("YYYY-MM-DD") ===
        dayjs(fechaSeleccionada).format("YYYY-MM-DD"),
    );

    if (turnos.length === 0) {
      return null;
    }

    return (
      <IonCol>
        <IonList lines="none">
          <IonListHeader>
            <Typography level="body-md">
              {`${obtenerNombreDia(
                dayjs(fechaSeleccionada).format("dddd"),
              )} ${dayjs(fechaSeleccionada).format("DD")} de ${dayjs(
                fechaSeleccionada,
              ).format("MMMM, YYYY")}`}
            </Typography>
          </IonListHeader>
          <IonList>
            <IonListHeader>
              <Typography level="body-md">Horarios disponibles</Typography>
            </IonListHeader>
            <IonGrid>
              <IonRow>
                {turnos.map((turno) => (
                  <Column container xs={6} md={3} key={turno.codigo}>
                    <StyledButton
                      expand="block"
                      className="blanco reserva-estudios-turno-btn"
                      onClick={() => handleClickSeleccionarTurno(turno)}
                    >
                      {dayjs(turno.hora).format("HH:mm")}
                    </StyledButton>
                  </Column>
                ))}
              </IonRow>
            </IonGrid>
          </IonList>
        </IonList>
      </IonCol>
    );
  };

  return (
    <>
      <Container container spacing={2} className="reserva-estudios-container">
        <Column xs={12}>
          <Card variant="outlined" className="reserva-estudios-card">
            <IonItem className="amarillo-sol reserva-estudios-selector">
              <CustomDesplegable
                array={estudios}
                value={estudioSeleccionado}
                handleChange={handleChangeSelect}
                mostrarTodos={false}
                mostrarSearch={true}
                placeholder={
                  estudios.length > 0
                    ? "Seleccione un estudio..."
                    : "No hay estudios disponibles"
                }
                label="Estudios"
                id="Estudio"
                isOpen={abrirDesplegableEstudios}
                setIsOpen={setAbrirDesplegableEstudios}
                variant="plain"
                disabled={estudios.length === 0}
              />
            </IonItem>
          </Card>
        </Column>

        {mostrarTurnoEstudioReservado && (
          <Column xs={12}>
            <Card
              variant="soft"
              color="warning"
              className="reserva-estudios-card"
            >
              <Typography level="title-sm">
                Ya tiene un turno futuro para este estudio
              </Typography>
              <Typography level="body-sm">
                No está permitido reservar otro turno del mismo estudio mientras
                el actual siga pendiente.
              </Typography>
            </Card>
          </Column>
        )}

        {estudioActual?.preparacion && (
          <Column xs={12}>
            <Card variant="outlined" className="reserva-estudios-card">
              <Typography level="title-sm" sx={{ color: "#B50009", mb: 1 }}>
                Preparación del estudio
              </Typography>
              <Typography level="body-sm">
                {convertToPlain(estudioActual.preparacion)}
              </Typography>
            </Card>
          </Column>
        )}

        {fechasFuturas.length > 0 && (
          <Column xs={12}>
            <Card variant="outlined" className="reserva-estudios-card">
              <Typography level="title-sm" sx={{ color: "#B50009", mb: 1 }}>
                Agenda del estudio
              </Typography>
              <div className="reserva-estudios-agenda-layout">
                <div className="reserva-estudios-agenda-panel reserva-estudios-calendario-panel">
                  <CustomCalendar
                    onChange={(newSelectedDate) =>
                      handleChangeFecha(
                        dayjs(newSelectedDate).format("YYYY-MM-DD"),
                      )
                    }
                    minDate={dayjs()}
                    maxDate={dayjs().add(3, "month")}
                    value={fechaSeleccionada}
                    highlightedDates={fechasFuturas}
                  />
                </div>
                <div className="reserva-estudios-agenda-panel reserva-estudios-horarios-panel">
                  {renderTurnos()}
                </div>
              </div>
            </Card>
          </Column>
        )}
      </Container>

      <DialogoConfirmacion
        titulo="Reservar turno"
        texto={`Ha seleccionado el turno para ${
          estudioActual?.nombre || estudioActual?.text || "el estudio"
        }${turnoSeleccionado.equipoNom ? ` en ${turnoSeleccionado.equipoNom}` : ""}, para el dia ${dayjs(
          turnoSeleccionado.fecha,
        ).format("DD/MM/YYYY")}, a las ${dayjs(turnoSeleccionado.hora).format(
          "HH:mm",
        )}, ¿es esto correcto?`}
        open={abrirModalConfirmarReserva}
        setOpen={setAbrirModalConfirmarReserva}
        handleClickCancelar={() => setAbrirModalConfirmarReserva(false)}
        handleClickConfirmar={handleClickConfirmarHacerReserva}
        colorBotonNo="danger"
        colorBotonSi="success"
        textoBotonNo="No, Revisar"
        textoBotonSi="Si, Confirmar"
      />

      <DialogoConfirmacion
        titulo="Turno reservado"
        texto={`Se ha reservado exitosamente el turno para ${
          estudioActual?.nombre || estudioActual?.text || "el estudio"
        }${turnoSeleccionado.equipoNom ? ` en ${turnoSeleccionado.equipoNom}` : ""}, para el dia ${dayjs(
          turnoSeleccionado.fecha,
        ).format(
          "DD/MM/YYYY",
        )}, a las ${dayjs(turnoSeleccionado.hora).format("HH:mm")}`}
        open={abrirModalPostReserva}
        setOpen={setAbrirModalPostReserva}
        handleClickConfirmar={() => handlePostTurnoReservado("nuevoTurno")}
        handleClickCancelar={() => handlePostTurnoReservado("turnosPaciente")}
        variantBotonNo="solid"
        colorBotonNo="primary"
        variantBotonSi="solid"
        colorBotonSi="primary"
        variantDialog="plain"
        colorDialogo="success"
        textoBotonNo="Consultar mis turnos"
        textoBotonSi="Reservar nuevo turno"
      />

      <CustomToast
        openToast={toast.open}
        onDidDismiss={() => mostrarNotificacion(false, "", "")}
        message={toast.mensaje}
        colorNotificacion={toast.tipo}
      />
      {(cargando || cargandoEstudios || cargandoAgenda) && (
        <LoadingBackdrop
          visualizar={cargando || cargandoEstudios || cargandoAgenda}
        />
      )}
    </>
  );
}
